import React from 'react';
import { Segment, Header, Icon, Button, Message, Input, Table, Modal, Form, Confirm } from 'semantic-ui-react';
import { PANELCHANGER_BASEURL } from '../../../constants/system';

function validateVisitorFields(form) {
  const visitorTerritory = (form?.visitorTerritory || '').trim();
  const rsm = (form?.rsm || '').trim();

  return {
    visitorTerritory,
    rsm,
    visitorTerritoryError: !visitorTerritory ? 'A terület megadása kötelező.' : null,
    rsmError: !rsm ? 'Az RSM megadása kötelező.' : null
  };
}

function deriveSandozEmailFromName(nameRaw) {
  const name = (nameRaw || '').trim().toLowerCase();
  if (!name) return '';
  const local = name
    .replace(/\s+/g, '.')
    .replace(/[^a-z0-9._-]/g, '')
    .replace(/\.+/g, '.')
    .replace(/^\./, '')
    .replace(/\.$/, '');
  if (!local) return '';
  return local + '@sandoz.net';
}

function getEmailLocalPart(emailRaw) {
  const email = (emailRaw || '').trim().toLowerCase();
  if (!email) return '';
  const at = email.indexOf('@');
  return at >= 0 ? email.slice(0, at) : email;
}

function getAdminUserIdentityWarning(form) {
  const name = (form?.name || '').trim();
  const alias = (form?.alias || '').trim();
  const email = (form?.email || '').trim();

  if (!name && !alias && !email) return null;

  const expectedEmail = deriveSandozEmailFromName(name);
  const emailLocal = getEmailLocalPart(email);
  const expectedLocal = getEmailLocalPart(expectedEmail);

  const parts = [];
  if (name && alias && name.toLowerCase() !== alias.toLowerCase()) parts.push('név ≠ alias');
  if (name && emailLocal && expectedLocal && emailLocal !== expectedLocal) parts.push('email eleje eltér a névtől');
  if (alias && emailLocal && emailLocal !== alias.trim().toLowerCase()) parts.push('email eleje eltér az alias-tól');

  if (parts.length === 0) return null;
  return (
    'Ritka eset, de eltérés van a név/alias/email eleje között (' +
    parts.join(', ') +
    '). Ha ez szándékos, figyelmen kívül hagyható.'
  );
}

function normalizeUser(u) {
  // backend returns PascalCase C# anonymous object -> JSON is usually camelCase in Azure Functions,
  // but we keep this defensive to avoid surprises.
  return {
    id: u.id ?? u.Id,
    name: u.name ?? u.Name,
    email: u.email ?? u.Email,
    role: u.role ?? u.Role,
    visitorTerritory: u.visitorTerritory ?? u.VisitorTerritory,
    line: u.line ?? u.Line,
    alias: u.alias ?? u.Alias,
    rsm: u.rsm ?? u.RSM,
    passwordExpiresAt: u.passwordExpiresAt ?? u.PasswordExpiresAt
  };
}

export default class AdminUsers extends React.Component {
  state = {
    loading: false,
    saving: false,
    error: undefined,
    q: '',
    users: [],

    createOpen: false,
    editOpen: false,
    deleteOpen: false,

    selectedUser: null,

    form: {
      name: '',
      email: '',
      role: 'Visitor',
      visitorTerritory: '',
      alias: '',
      rsm: '',
      password: ''
    }
  };

  createNameEl = null;
  editNameEl = null;

  focusCreateName = () => window.setTimeout(() => this.createNameEl?.focus?.(), 50);
  focusEditName = () => window.setTimeout(() => this.editNameEl?.focus?.(), 50);

  componentDidMount() {
    this.refresh();
  }

  refresh = () => {
    this.setState({ loading: true, error: undefined });
    fetch(PANELCHANGER_BASEURL + '/management/users', {
      headers: { Token: localStorage.getItem('token') }
    })
      .then(async (r) => {
        if (r.status === 401) throw new Error('Nincs jogosultság.');
        if (!r.ok) throw new Error('Hiba a listázás során (' + r.status + ').');
        const data = await r.json();
        const users = Array.isArray(data) ? data.map(normalizeUser) : [];
        this.setState({ users, loading: false });
      })
      .catch((e) => this.setState({ error: e.message, loading: false }));
  };

  openCreate = () => {
    this.setState({
      createOpen: true,
      error: undefined,
      form: { name: '', email: '', role: 'Visitor', visitorTerritory: '', alias: '', rsm: '', password: '' }
    }, () => {
      this.focusCreateName();
    });
  };

  openEdit = (u) => {
    this.setState({
      editOpen: true,
      error: undefined,
      selectedUser: u,
      form: {
        name: u.name || '',
        email: u.email || '',
        role: u.role || 'Visitor',
        visitorTerritory: u.visitorTerritory || '',
        alias: u.alias || '',
        rsm: u.rsm || '',
        password: ''
      }
    }, () => {
      this.focusEditName();
    });
  };

  openDelete = (u) => {
    this.setState({ deleteOpen: true, selectedUser: u, error: undefined });
  };

  closeModals = () => {
    this.setState({ createOpen: false, editOpen: false, deleteOpen: false, selectedUser: null });
  };

  setForm = (patch) => {
    this.setState({ form: { ...this.state.form, ...patch } });
  };

  setCreateName = (name) => {
    const email = deriveSandozEmailFromName(name);
    this.setForm({ name, alias: name, email });
  };

  submitCreate = () => {
    const v = validateVisitorFields(this.state.form);
    const body = {
      name: (this.state.form.name || '').trim(),
      email: (this.state.form.email || '').trim() || null,
      role: 'Visitor',
      visitorTerritory: v.visitorTerritory || null,
      alias: (this.state.form.alias || '').trim() || null,
      rsm: v.rsm || null
    };

    if (!body.name) return this.setState({ error: 'A név megadása kötelező.' });
    if (v.visitorTerritoryError) return this.setState({ error: v.visitorTerritoryError });
    if (v.rsmError) return this.setState({ error: v.rsmError });

    this.setState({ saving: true, error: undefined });
    fetch(PANELCHANGER_BASEURL + '/management/users', {
      method: 'POST',
      headers: {
        Token: localStorage.getItem('token'),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    })
      .then(async (r) => {
        if (r.status === 409) {
          const j = await r.json().catch(() => null);
          throw new Error((j && (j.message || j.Message)) || 'Már létezik ilyen felhasználó.');
        }
        if (r.status === 401) throw new Error('Nincs jogosultság.');
        if (!r.ok) {
          const txt = await r.text().catch(() => '');
          throw new Error(txt || 'Hiba mentés közben (' + r.status + ').');
        }
        return r.json();
      })
      .then(() => {
        this.setState({ saving: false, createOpen: false });
        this.refresh();
      })
      .catch((e) => this.setState({ error: e.message, saving: false }));
  };

  submitEdit = () => {
    const u = this.state.selectedUser;
    if (!u) return;

    const v = validateVisitorFields(this.state.form);
    const body = {
      name: (this.state.form.name || '').trim(),
      email: (this.state.form.email || '').trim() || null,
      visitorTerritory: v.visitorTerritory || null,
      alias: (this.state.form.alias || '').trim() || null,
      rsm: v.rsm || null
    };

    if (!body.name) return this.setState({ error: 'A név megadása kötelező.' });
    if (v.visitorTerritoryError) return this.setState({ error: v.visitorTerritoryError });
    if (v.rsmError) return this.setState({ error: v.rsmError });

    this.setState({ saving: true, error: undefined });
    fetch(PANELCHANGER_BASEURL + '/management/users/' + u.id, {
      method: 'PUT',
      headers: {
        Token: localStorage.getItem('token'),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    })
      .then(async (r) => {
        if (r.status === 409) {
          const j = await r.json().catch(() => null);
          throw new Error((j && (j.message || j.Message)) || 'Már létezik ilyen felhasználó.');
        }
        if (r.status === 401) throw new Error('Nincs jogosultság.');
        if (!r.ok) {
          const txt = await r.text().catch(() => '');
          throw new Error(txt || 'Hiba mentés közben (' + r.status + ').');
        }
        return r.json();
      })
      .then(() => {
        this.setState({ saving: false, editOpen: false, selectedUser: null });
        this.refresh();
      })
      .catch((e) => this.setState({ error: e.message, saving: false }));
  };

  confirmDelete = () => {
    const u = this.state.selectedUser;
    if (!u) return;

    this.setState({ saving: true, error: undefined });
    fetch(PANELCHANGER_BASEURL + '/management/users/' + u.id, {
      method: 'DELETE',
      headers: { Token: localStorage.getItem('token') }
    })
      .then(async (r) => {
        if (r.status === 401) throw new Error('Nincs jogosultság.');
        if (!r.ok) {
          const txt = await r.text().catch(() => '');
          throw new Error(txt || 'Hiba törlés közben (' + r.status + ').');
        }
        return r.json().catch(() => ({}));
      })
      .then(() => {
        this.setState({ saving: false, deleteOpen: false, selectedUser: null });
        this.refresh();
      })
      .catch((e) => this.setState({ error: e.message, saving: false }));
  };

  render() {
    const q = (this.state.q || '').trim().toLowerCase();
    const filtered = q
      ? this.state.users.filter((u) => {
          const hay = [u.name, u.email, u.role, u.visitorTerritory, u.line, u.rsm, u.alias].filter(Boolean).join(' ').toLowerCase();
          return hay.includes(q);
        })
      : this.state.users;

    const createValidation = this.state.createOpen ? validateVisitorFields(this.state.form) : null;
    const editValidation = this.state.editOpen ? validateVisitorFields(this.state.form) : null;
    const createName = (this.state.form.name || '').trim();
    const editName = (this.state.form.name || '').trim();
    const canSubmitCreate = !!createName && !createValidation?.visitorTerritoryError && !createValidation?.rsmError && !this.state.saving;
    const canSubmitEdit = !!editName && !editValidation?.visitorTerritoryError && !editValidation?.rsmError && !this.state.saving;

    return (
      <Segment basic loading={this.state.loading || this.state.saving}>
        {this.state.error ? (
          <Message error onDismiss={() => this.setState({ error: undefined })}>
            Hiba történt. {this.state.error}
          </Message>
        ) : (
          ''
        )}

        <div
          className="pc-admin-users-toolbar"
          style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', justifyContent: 'space-between' }}
        >
          <Header as="h3" style={{ marginBottom: 0 }}>
            <Icon name="users" /> Felhasználók
          </Header>
          <div className="pc-admin-users-toolbar-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'flex-end' }}>
            <Input
              placeholder="Keresés…"
              value={this.state.q}
              onChange={(e) => this.setState({ q: e.target.value })}
              className="pc-admin-users-search"
            />
            <Button basic primary onClick={this.refresh} className="pc-admin-users-refresh">
              <Icon name="refresh" /> Frissítés
            </Button>
            <Button primary onClick={this.openCreate} className="pc-admin-users-create">
              <Icon name="plus" /> Új user
            </Button>
          </div>
        </div>

        <Table celled compact style={{ marginTop: '12px' }} className="pc-responsive-table pc-admin-users-table">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Név</Table.HeaderCell>
              <Table.HeaderCell>Role</Table.HeaderCell>
              <Table.HeaderCell>Email</Table.HeaderCell>
              <Table.HeaderCell>Terület</Table.HeaderCell>
              <Table.HeaderCell>Line</Table.HeaderCell>
              <Table.HeaderCell>RSM</Table.HeaderCell>
              <Table.HeaderCell>Alias</Table.HeaderCell>
              <Table.HeaderCell textAlign="right">Művelet</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {filtered.map((u) => (
              <Table.Row key={u.id}>
                <Table.Cell data-label="Név">{u.name || '—'}</Table.Cell>
                <Table.Cell data-label="Role">{u.role || '—'}</Table.Cell>
                <Table.Cell data-label="Email" style={{ wordBreak: 'break-word' }}>
                  {u.email || '—'}
                </Table.Cell>
                <Table.Cell data-label="Terület">{u.visitorTerritory || '—'}</Table.Cell>
                <Table.Cell data-label="Line">{u.line || '—'}</Table.Cell>
                <Table.Cell data-label="RSM">{u.rsm || '—'}</Table.Cell>
                <Table.Cell data-label="Alias">{u.alias || '—'}</Table.Cell>
                <Table.Cell data-label="Művelet" textAlign="right">
                  <div className="pc-admin-users-actions">
                    <Button size="small" basic onClick={() => this.openEdit(u)}>
                      <Icon name="edit" /> Módosít
                    </Button>
                    <Button size="small" basic negative onClick={() => this.openDelete(u)} disabled={this.props.myUserId === u.id}>
                      <Icon name="trash" /> Töröl
                    </Button>
                  </div>
                </Table.Cell>
              </Table.Row>
            ))}
            {filtered.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan="8">
                  <Message info style={{ margin: 0 }}>
                    Nincs találat.
                  </Message>
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table>

        <Modal open={this.state.createOpen} onClose={this.closeModals} onOpen={this.focusCreateName} size="small" closeIcon>
          <Modal.Header>Új felhasználó</Modal.Header>
          <Modal.Content>
            <Form>
              <Form.Field required>
                <label>Név</label>
                <Input
                  value={this.state.form.name}
                  onChange={(e) => this.setCreateName(e.target.value)}
                  inputRef={(el) => (this.createNameEl = el)}
                />
              </Form.Field>
              <Message info>
                Jelszót itt nem kell megadni. Az első belépéshez a felhasználó a „Forgot password / Reset password” folyamattal állít be jelszót.
              </Message>
              <Form.Field>
                <label>Email</label>
                <Input value={this.state.form.email} readOnly />
              </Form.Field>
              <Form.Field required>
                <label>Role</label>
                <Input value="Visitor" readOnly />
              </Form.Field>
              <Form.Field required error={!!createValidation?.visitorTerritoryError}>
                <label>Visitor terület</label>
                <Input value={this.state.form.visitorTerritory} onChange={(e) => this.setForm({ visitorTerritory: e.target.value })} />
              </Form.Field>
              <Form.Field>
                <label>Line</label>
                <Input value="Importból automatikus" readOnly />
              </Form.Field>
              <Form.Field required error={!!createValidation?.rsmError}>
                <label>RSM</label>
                <Input value={this.state.form.rsm} onChange={(e) => this.setForm({ rsm: e.target.value })} />
              </Form.Field>
              <Form.Field>
                <label>Alias</label>
                <Input value={this.state.form.alias} readOnly />
              </Form.Field>
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={this.closeModals}>Mégsem</Button>
            <Button primary onClick={this.submitCreate} loading={this.state.saving} disabled={!canSubmitCreate}>
              <Icon name="save" /> Létrehoz
            </Button>
          </Modal.Actions>
        </Modal>

        <Modal open={this.state.editOpen} onClose={this.closeModals} onOpen={this.focusEditName} size="small" closeIcon>
          <Modal.Header>Felhasználó módosítása</Modal.Header>
          <Modal.Content>
            <Message info>
              A jelszó itt nem módosítható. (Ha kell, reset/forgot-password flow-val.)
            </Message>
            {getAdminUserIdentityWarning(this.state.form) ? (
              <Message
                warning
                style={{
                  border: '1px solid rgba(240, 173, 78, 0.8)',
                  borderLeft: '8px solid #f0ad4e',
                  background: 'rgba(240, 173, 78, 0.16)',
                  color: 'inherit',
                  fontWeight: 600
                }}
              >
                <Icon name="warning sign" />
                {getAdminUserIdentityWarning(this.state.form)}
              </Message>
            ) : (
              ''
            )}
            <Form>
              <Form.Field required>
                <label>Név</label>
                <Input
                  value={this.state.form.name}
                  onChange={(e) => this.setForm({ name: e.target.value })}
                  inputRef={(el) => (this.editNameEl = el)}
                />
              </Form.Field>
              <Form.Field>
                <label>Email</label>
                <Input value={this.state.form.email} onChange={(e) => this.setForm({ email: e.target.value })} />
              </Form.Field>
              <Form.Field required>
                <label>Role</label>
                <Input value={this.state.form.role || ''} readOnly />
              </Form.Field>
              <Form.Field required error={!!editValidation?.visitorTerritoryError}>
                <label>Visitor terület</label>
                <Input value={this.state.form.visitorTerritory} onChange={(e) => this.setForm({ visitorTerritory: e.target.value })} />
              </Form.Field>
              <Form.Field>
                <label>Line</label>
                <Input value={this.state.selectedUser?.line || '—'} readOnly />
              </Form.Field>
              <Form.Field required error={!!editValidation?.rsmError}>
                <label>RSM</label>
                <Input value={this.state.form.rsm} onChange={(e) => this.setForm({ rsm: e.target.value })} />
              </Form.Field>
              <Form.Field>
                <label>Alias</label>
                <Input value={this.state.form.alias} onChange={(e) => this.setForm({ alias: e.target.value })} />
              </Form.Field>
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={this.closeModals}>Mégsem</Button>
            <Button primary onClick={this.submitEdit} loading={this.state.saving} disabled={!canSubmitEdit}>
              <Icon name="save" /> Mentés
            </Button>
          </Modal.Actions>
        </Modal>

        <Confirm
          open={this.state.deleteOpen}
          onCancel={this.closeModals}
          onConfirm={this.confirmDelete}
          cancelButton="Mégsem"
          confirmButton="Igen, törlöm"
          content={
            this.state.selectedUser
              ? 'Biztosan törlöd a felhasználót: ' + (this.state.selectedUser.name || '—') + '?'
              : 'Biztosan törlöd?'
          }
        />
      </Segment>
    );
  }
}

